// src/components/Dropdown.js
import { useState, useRef, useEffect } from "react";
import arrowIcon from "../assets/down-arrow.png";
import { useTranslation } from "react-i18next";
import { req_Status } from "../utils/enums";

const Dropdown = ({
  options,
  selected,
  setSelected,
  thick,
  flex,
  errorText,
  onChange,
  page = "invoices",
  getData = null,
  nameInAr = false,
  disabled,
  titleCssClass,
  prefetch = false,
  // this prop is to prevent re-selecting the same item in case of single select dropdown
  allowSameSelect = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isFirstClick = useRef(true);
  const { t } = useTranslation("general");
  const [loading, setLoading] = useState({ status: req_Status.NULL });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleDropdown = async () => {
    if (disabled) return;

    setIsOpen(!isOpen);

    if (getData && isFirstClick.current) {
      setLoading((prev) => ({ ...prev, status: req_Status.PENDING }));
      const req = await getData();
      isFirstClick.current = false;

      if (req?.status === 200) {
        setLoading((prev) => ({ ...prev, status: req_Status.SUCCESS }));
      } else {
        setLoading((prev) => ({ ...prev, status: req_Status.FAILED }));
      }
    }
  };

  const handleItemClick = (item) => {
    if (selected?.id !== item.id && !allowSameSelect) setSelected(item, page);
    if (allowSameSelect) setSelected(item, page);
    if (onChange) {
      onChange(item);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      toggleDropdown();
    }
  };

  const showError = !selected?.value && errorText;

  useEffect(() => {
    if (prefetch && getData) {
      setLoading((prev) => ({ ...prev, status: req_Status.PENDING }));
      getData().then((req) => {
        isFirstClick.current = false; // Mark as fetched
        if (req?.status === 200) {
          setLoading((prev) => ({ ...prev, status: req_Status.SUCCESS }));
        } else {
          setLoading((prev) => ({ ...prev, status: req_Status.FAILED }));
        }
      });
    }
  }, [prefetch]);

  return (
    <div
      className={`c-dropdown ${flex ? "full-width-dropdown" : ""}`}
      ref={dropdownRef}
    >
      {showError && <div className="error-message">{t(errorText)}</div>}
      <div
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0} // Make the dropdown toggle focusable
        className={`toggle ${isOpen ? "opened" : ""} ${thick ? "thick" : ""}`}
        style={disabled ? { cursor: "not-allowed", opacity: 0.6 } : {}}
      >
        {!nameInAr ? (
          options && typeof selected === "object" && selected !== null ? (
            <p className="t-text-mini">
              {selected?.title ? t(selected.title) : selected?.name}
            </p>
          ) : null
        ) : options && typeof selected === "object" && selected !== null ? (
          <p className="text">
            {selected?.title
              ? t(selected.title)
              : nameInAr
              ? selected?.nameInAr
              : selected?.name}
          </p>
        ) : null}
        <img
          src={arrowIcon}
          alt="Dropdown arrow"
          className={`arrow-icon ${isOpen ? "rotate180" : ""}`}
        />
      </div>

      {isOpen && (
        <>
          <ul className="dropdown-menu">
            {Array.isArray(options) &&
              options.map((option, index) => (
                <li
                  key={index}
                  className={`${titleCssClass} dropdown-item`}
                  onClick={() => handleItemClick(option)}
                  tabIndex={0} // Make each dropdown item focusable
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleItemClick(option);
                    }
                  }}
                >
                  {!nameInAr
                    ? typeof option === "object"
                      ? t(option?.title) || option?.name
                      : option
                    : typeof option === "object"
                    ? t(option?.title) || option?.nameInAr
                    : option}
                </li>
              ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default Dropdown;
